import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Card } from '@factorialco/f0-react'
import { TwoColumnsList } from '@factorialco/f0-react/dist/experimental'
import { AttendanceCustomPeriodEmployeeItemFragment } from 'generated/resources/graphql'

import { minutesToText } from 'modules/core/lib/duration'
import i18n from 'modules/core/lib/i18n'

import { aggregateMinutes } from 'modules/attendance/components/TimeTrackingCollection/lib/aggregateMinutes'

interface OverviewTabProps {
  employee: AttendanceCustomPeriodEmployeeItemFragment
}

const ExtraHoursEmptyState = ({ readyDate }: { readyDate?: string }) => {
  const emptyStateMessage = readyDate
    ? i18n.t('time_tracking.table_collection.employee_summary.extra_hours.ready_on_date', {
        date: readyDate,
      })
    : i18n.t('time_tracking.table_collection.employee_summary.extra_hours.ready_soon')

  return (
    <Box textAlign="center">
      <F0Card
        title={i18n.t(
          'time_tracking.table_collection.employee_summary.extra_hours.calculation_in_progress'
        )}
        description={emptyStateMessage}
        compact
      />
    </Box>
  )
}

export const OverviewTab = ({ employee }: OverviewTabProps) => {
  const {
    plannedMinutes,
    balanceMinutes,
    dailyDays,
    effectiveWorkedMinutes,
    paidAbsencesMinutes,
    paidBreaksMinutes,
    workedMinutes,
    extraTime,
    regularMinutes,
    baseline,
    extraHoursCalculationOn,
  } = aggregateMinutes(
    employee,
    employee.attendanceEmployeesPolicy?.attendanceEmployeesSettingsConnection?.edges?.[0]?.node
      ?.extraHoursBaseline
  )

  const workedMinutesFormatted = minutesToText(workedMinutes, true)
  const effectiveWorkedMinutesFormatted = minutesToText(effectiveWorkedMinutes, true)

  const extraTimeMinutes = extraTime.minutes
  const hasBaseline = baseline.type !== undefined
  const baselineLabel =
    baseline.type === 'contract_hours'
      ? i18n.t('time_tracking.table_collection.employee_summary.contract_hours')
      : i18n.t('time_tracking.table_collection.employee_summary.planned_hours')

  const comparedLabel =
    baseline.type === 'contract_hours'
      ? i18n.t('time_tracking.table_collection.employee_summary.effective_worked_time')
      : i18n.t('time_tracking.table_collection.employee_summary.worked_time')

  const isDaily = Boolean(employee.referenceContract?.frForfaitJours)
  const readyDate = extraTimeMinutes === undefined ? extraHoursCalculationOn : undefined

  return (
    <>
      <TwoColumnsList
        title={i18n.t('time_tracking.table_collection.employee_summary.worked_time')}
        titleValue={
          isDaily
            ? i18n.t('time_tracking.day_abbreviation', {
                numberOfDays: dailyDays,
              })
            : workedMinutesFormatted
        }
        titleTooltip={{
          label: i18n.t(
            'time_tracking.table_collection.employee_summary.tooltips.worked_time.label'
          ),
          description: i18n.t(
            'time_tracking.table_collection.employee_summary.tooltips.worked_time.description'
          ),
        }}
        list={
          isDaily
            ? []
            : [
                {
                  title: i18n.t('time_tracking.table_collection.employee_summary.regular_hours'),
                  info: minutesToText(regularMinutes, true),
                },
                {
                  title: i18n.t('time_tracking.table_collection.employee_summary.paid_breaks'),
                  info: minutesToText(paidBreaksMinutes, true),
                },
                {
                  title: i18n.t(
                    'time_tracking.table_collection.employee_summary.extra_hours.title'
                  ),
                  info:
                    extraTimeMinutes === undefined ? '-' : minutesToText(extraTimeMinutes, true),
                },
              ]
        }
      />
      {isDaily ? (
        <></>
      ) : (
        <>
          <TwoColumnsList
            title={i18n.t('time_tracking.table_collection.employee_summary.effective_worked_time')}
            titleValue={effectiveWorkedMinutesFormatted}
            titleTooltip={{
              label: i18n.t(
                'time_tracking.timesheet.sidepanel.tooltips.effective_worked_time.label'
              ),
              description: i18n.t(
                'time_tracking.timesheet.sidepanel.tooltips.effective_worked_time.description'
              ),
            }}
            list={[
              {
                title: i18n.t('time_tracking.table_collection.employee_summary.worked_time'),
                info: workedMinutesFormatted,
              },
              {
                title: i18n.t('time_tracking.table_collection.employee_summary.paid_absences'),
                info: minutesToText(paidAbsencesMinutes, true),
              },
            ]}
          />
          <TwoColumnsList
            title={i18n.t('time_tracking.table_collection.employee_summary.balance')}
            titleValue={minutesToText(balanceMinutes, true, true)}
            titleTooltip={{
              label: i18n.t('time_tracking.timesheet.sidepanel.tooltips.balance.label'),
              description: i18n.t('time_tracking.timesheet.sidepanel.tooltips.balance.description'),
            }}
            list={[
              {
                title: i18n.t('time_tracking.table_collection.employee_summary.planned_time'),
                info: minutesToText(plannedMinutes, true),
              },
              {
                title: i18n.t('time_tracking.table_collection.employee_summary.worked_time'),
                info: workedMinutesFormatted,
              },
            ]}
          />
          <TwoColumnsList
            title={i18n.t('time_tracking.table_collection.employee_summary.extra_hours.title')}
            titleValue={
              extraTimeMinutes === undefined ? '-' : minutesToText(extraTimeMinutes, true)
            }
            titleTooltip={{
              label: i18n.t('time_tracking.timesheet.sidepanel.tooltips.extra_hours.label'),
              description: i18n.t(
                'time_tracking.timesheet.sidepanel.tooltips.extra_hours.description'
              ),
            }}
            list={
              extraTimeMinutes === undefined
                ? []
                : [
                    {
                      title: baselineLabel,
                      info: hasBaseline ? minutesToText(baseline.baselineMinutes, true) : '-',
                    },
                    {
                      title: comparedLabel,
                      info: hasBaseline ? minutesToText(baseline.comparedMinutes, true) : '-',
                    },
                  ]
            }
          />
          {extraTimeMinutes === undefined && hasBaseline && (
            <ExtraHoursEmptyState readyDate={readyDate} />
          )}
        </>
      )}
    </>
  )
}
